/* @flow */

import adconfig from './adconfig';
import google from './adserver/googleAS';
import staticAS from './adserver/staticas';
import ezoicAS from './adserver/ezoicAS';
import { logError, logDebug, logInfo } from './utils/logger';
import { extend, difference, intersect } from './utils/coreutils';
import { getEvaluatedDialup } from './utils/browserutil';

import bm from './bidmanager-facade';
import getCVars from './utils/gputils';

/**
 * @description Implements ad server functionality
 * @class
 * @exports adserver
 * @requires  module:coreutils
 * @requires  module:adserver/google
 * @requires  module:adserver/staticas
 * @requires  module:adserver/ezoicAS
 * @requires  module:adconfig
 * @requires  module:bidmanager
 * @requires module:logger
 */
class _adserver {
  _name: string;

  _doc: Document;

  _win: WindowProxy;

  _adservers: Array<adServerInterface>;

  _staticCustomVars: CustomVars;

  _customVars: CustomVars;

  _posToAdServerMap: Map<string, string>;

  _site: string;

  _isDialup: boolean;

  _bInited: boolean;

  constructor() {
    this._name = 'adserver';
    this._doc = document;
    this._win = window;
    this._staticCustomVars = {};
    this._customVars = {};
    this._posToAdServerMap = new Map();
    this._site = '';
    this._isDialup = false;
    this._bInited = false;
  }

  /**
   * Initialize the object
   *
   * @param {window} w - window object
   * @param {Document} d - document object
   * @param {module:globalParams.globalParams} g - global param object
   * @returns {undefined}
   */
  init(w: WindowProxy, d: Document, g: globalParam) {
    this._doc = d;
    this._win = w;
    this._staticCustomVars = g.customVars;
    const servers: Array<adServerInterface> = [staticAS, google, ezoicAS];
    this._adservers = [];
    servers.forEach(v => {
      if (v._isSupported()) {
        this._adservers.push(v);
      }
    });
    this._adservers.forEach(as => as.init(w, d, g));
    this._site = g.phInfo.site;
    this._isDialup = getEvaluatedDialup(g.isDialup);
    // initialize bidmanager
    bm.init(g);
    this._bInited = true;
  }

  /**
   * Update config - called adserver updateConfig
   *
   * @external adServerConfig
   * @param {adServerConfig} o - object with config for each adserver from adconfig
   * @returns {undefined}
   */
  updateConfig(o: adServerConfig) {
    if (!this._bInited) {
      return;
    }
    // Rebuild position -> ad server mapping cache for faster lookups
    // Reference: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Map
    this._posToAdServerMap.clear();
    const { settings: { posMap = {} } = {} } = adconfig.get() || {};
    Object.keys(posMap).forEach(pos => {
      const posSettings = posMap[pos];
      if (posSettings && posSettings.as) {
        this._posToAdServerMap.set(pos, posSettings.as);
      }
    });

    this._adservers.forEach(as => as.updateConfig(o[as.getName()]));
  }

  /**
   * get custom variables
   *
   * @returns {object} - Custom variables to send along with ad request
   * @access private
   */
  _getCustomVars(): CustomVars {
    const ret = getCVars(this._win);
    // merge user provided customVar
    extend(true, ret, this._customVars);
    return ret;
  }

  /**
   * load ads
   *
   * @param {Array<string>} posList - Ad position list
   * @external hbPartnerData
   * @param {hbPartnerData} o - HB data
   * @param {boolean} bRotation - For ad rotation?
   * @returns {undefined}
   * @access private
   */
  _loadAds(
    posList: Array<string> = [],
    o: hbPartnerData = {},
    bRotation: boolean = false
  ) {
    const asList = [];
    const cumPosList = this._adservers.flatMap(as => {
      const asName = as.getName();
      const asPosList = posList.filter(
        pos => this._posToAdServerMap.get(pos) === asName
      );
      if (asPosList.length) {
        as.loadAds(asPosList, o, this._getCustomVars(), bRotation);
        asList.push(`${asName}:${asPosList.join(',')}`);
      }
      return asPosList;
    });
    logInfo(
      `AdsLoaded ${asList.join('|')}&site=${this._site}&du=${this._isDialup ? 1 : 0}&pn=${adconfig.getPageName()}&ar=${bRotation ? 1 : 0}`
    );
    const misPos = difference(posList, cumPosList).join(',');
    if (misPos.length) {
      logError(`adserver is not configured for ${misPos}`);
    }
  }

  refreshPrivacySignals() {
    if (!this._bInited) {
      return;
    }
    this._adservers.forEach(as => {
      as.refreshPrivacySignals();
    });
  }

  clearAdRequests(
    pageName: string,
    posList: Array<string> = [],
    bRotation: boolean = false
  ) {
    if (!this._bInited) {
      return;
    }
    // invoke the same in each listed adserver (only when that server has positions in this request)
    this._adservers.forEach(as => {
      const asName = as.getName();
      const asPosList = posList.filter(
        pos => this._posToAdServerMap.get(pos) === asName
      );
      if (asPosList.length) {
        as.clearAdRequests(pageName, asPosList, bRotation);
      }
    });
  }

  /**
   * Bid and load ads
   *
   * @param {string} pageName - Page name
   * @param {Array<string>} posListArr - List of ad positions
   * @param {boolean} bRotation - For ad rotation?
   * @returns {undefined}
   */
  loadAds(pageName: string, posListArr: Array<string>, bRotation: boolean) {
    if (!this._bInited) {
      logError(
        `${this._name} loadAds called before init; skipping. pn=${pageName}&pos=${posListArr.join(
          ','
        )}&ar=${bRotation ? 1 : 0}`
      );
      return;
    }
    const activeBidPos = intersect(posListArr, bm.getBidPos(pageName));
    const inactiveBidPos = difference(posListArr, activeBidPos);
    logDebug(
      `active bid pos:${activeBidPos.join(
        ','
      )} inactive bid pos:${inactiveBidPos.join(',')}`
    );

    // load inactive static ad positions immediately
    if (inactiveBidPos.length) {
      this._loadAds(inactiveBidPos, {}, bRotation);
    }

    // perform header bidding for remaining positions
    if (activeBidPos.length > 0) {
      bm.getBids(activeBidPos, pageName, bRotation).then(
        o => this._loadAds(activeBidPos, o, bRotation),
        () => this._loadAds(activeBidPos, {}, bRotation)
      );
    }
  }

  /**
   *
   * Render ad from the bidder
   *
   * @param {window} win - IFrame window object
   * @param {Document} doc - IFrame document
   * @param {string} pos - Ad position
   * @param {string} partner - Partner won the bid
   * @param {string} param - Extra params
   * @returns {undefined}
   * @memberof _adserver
   */
  renderBidAds(
    win: WindowProxy,
    doc: Document,
    pos: string,
    partner: string,
    param: string
  ) {
    bm.renderAd(win, doc, pos, partner, param, adconfig.getPosSettings(pos));
  }

  /**
   * set custom var
   *
   * @param {string} k - Custom Var key
   * @param {object} v - Custom var value
   * @returns {undefined}
   * @access private
   */
  setCustomVar(k: string, v: unknown) {
    const key = k || '';
    if (key.length) {
      this._customVars[key] = v;
    }
  }
}

// export singleton object as default for this module
const as: _adserver = new _adserver();
export default as;
